import { formatHumanDuration } from "@formant/date-fns/formatHumanDuration";
import { duration } from "@formant/core/model/duration";
import { HealthStatus } from "@formant/core/model/HealthStatus";

import { IMetadataDataPoint } from "../utils/IMetadataDataPoint";

type StatusReport = [status: HealthStatus, items: string[]];

export interface IDeviceReportFn {
    (prev: StatusReport, point: IMetadataDataPoint<"health">): StatusReport;
}

export function statusChecker(prev: StatusReport): StatusReport {
    const [deviceStatus, items] = prev;
    if (!["error", "unknown"].includes(deviceStatus)) {
        return prev;
    }

    return [
        deviceStatus,
        [
            ...items,
            `Device is in an ${deviceStatus} state, please view device logs for further details.`,
        ],
    ];
}

const maxAllowedSkew = 30 * duration.second;

export function skewChecker(
    prev: StatusReport,
    point: IMetadataDataPoint<"health">,
): StatusReport {
    const skewMs = point.value.clockSkewMs;
    if (!(skewMs && Math.abs(skewMs) > maxAllowedSkew)) {
        return prev;
    }

    const direction = skewMs > 0 ? "behind" : "ahead";
    const amount = formatHumanDuration(Math.floor(skewMs / duration.second));

    const [, items] = prev;
    return [
        "error",
        [
            ...items,
            `Device clock is out of sync (running ${direction} by ${amount}), please re-sync the system time on your agent's host machine.`,
        ],
    ];
}

export const createDeviceReporter =
    (...checkers: IDeviceReportFn[]) =>
    (point: IMetadataDataPoint<"health">): [string, string | undefined] => {
        const { value } = point;
        const { status: initialStatus } = value;

        const [displayStatus, deviceReport] = checkers.reduce<StatusReport>(
            (prev, item) => item(prev, point),
            [initialStatus, []],
        );

        return [
            displayStatus,
            deviceReport.length ? deviceReport.join(" ") : undefined,
        ];
    };
